'use client'

import Image from '@/components/image'
import Link from '@/components/link-locale'
import { useRouter } from '@/i18n/routing'
import { Select } from 'antd'
import { useLocale, useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import React from 'react'
import {
  AiOutlineInstagram,
  AiOutlineTikTok,
  AiOutlineTwitter,
} from 'react-icons/ai'

function NavItem({ children, href, disabled = true, isExternalLink = false }) {
  if (disabled) {
    return <span className='cursor-not-allowed text-secondary'>{children}</span>
  }

  if (isExternalLink) {
    return (
      <Link
        href={href}
        className='text-[#F9F9F9] hover:text-[#FF3C8E]'
        target='_blank'
        rel='noreferrer'
      >
        {children}
      </Link>
    )
  }

  return (
    <Link
      href={href}
      className='text-[#F9F9F9] hover:text-[#FF3C8E]'
      target='_blank'
    >
      {children}
    </Link>
  )
}

function LogoBlock() {
  return (
    <div className='flex flex-col gap-y-5 xl:justify-between'>
      <Link href='/'>
        <Image
          src='/logo.svg'
          alt='Mfans'
          width={327}
          height={120}
          className='h-12 w-auto xl:h-20'
        />
      </Link>
      <Image
        src='/rta.png'
        alt='RTA'
        width={84}
        height={40}
        className='h-auto w-[68px]'
      />
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function NavBlock1() {
  const t = useTranslations()
  return (
    <div className='flex flex-col gap-y-5 text-xs font-semibold xl:text-sm'>
      <hr className='w-7 border-t-4 border-primary' />
      <NavItem disabled={false} href='/info/faq'>
        {t('footer.faq')}
      </NavItem>
      <NavItem href='/info/contact' disabled={false}>
        {t('footer.contactUs')}
      </NavItem>
      <NavItem href='/info/about'>{t('footer.about')}</NavItem>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function NavBlock2() {
  const t = useTranslations()
  return (
    <div className='flex flex-col gap-y-5 text-xs font-semibold xl:text-sm'>
      <hr className='w-7 border-t-4 border-primary' />
      <NavItem disabled={false} href='/info/privacy'>
        {t('footer.privacy')}
      </NavItem>
      <NavItem disabled={false} href='/info/terms'>
        {t('footer.terms')}
      </NavItem>
      <NavItem disabled={false} href='/info/contract'>
        {t('footer.contract')}
      </NavItem>
      <NavItem disabled={false} href='/info/contractCreator'>
        {t('footer.contractCreator')}
      </NavItem>
      <NavItem disabled={false} href='/info/usc2257'>
        {t('footer.usc2257')}
      </NavItem>
    </div>
  )
}

function SocialBlock({ handleLanguageChange }) {
  const locale = useLocale()
  const SocialItems = [
    {
      icon: <AiOutlineTikTok />,
      href: 'https://www.tiktok.com/@mfans.official',
    },
    {
      icon: <AiOutlineTwitter />,
      href: 'https://x.com/MfansofficialX',
    },
    {
      icon: <AiOutlineInstagram />,
      href: 'https://www.instagram.com/mfans.official/',
    },
  ]

  return (
    <div className='flex flex-col gap-y-8 xl:gap-y-10'>
      <div className='flex gap-x-5 text-xl xl:text-3xl'>
        {SocialItems.map(({ href, icon }) => (
          <Link key={href} href={href} target='_blank'>
            <div className='flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white hover:bg-white hover:text-primary xl:h-10 xl:w-10'>
              {icon}
            </div>
          </Link>
        ))}
      </div>
      <Select
        value={locale}
        onChange={handleLanguageChange}
        options={[
          { value: 'zh-TW', label: '繁中' },
          { value: 'zh-CN', label: '简中' },
          { value: 'en-US', label: 'English' },
        ]}
      />
    </div>
  )
}

function VersionBlock() {
  const t = useTranslations()
  return (
    <div className='w-4/5 text-center text-[10px] font-normal text-[#EAEAEA]'>
      <p>{t('footer.version')}</p>
      <p className='mt-2'>{t('footer.copyright')}</p>
    </div>
  )
}

export default function Footer() {
  const router = useRouter()
  const locale = useLocale()
  const pathname = usePathname()

  const handleLanguageChange = value => {
    const href = pathname.replace(`/${locale}`, '/').replace('//', '/')
    router.replace(href, { locale: value })
  }

  return (
    <div className='flex w-full flex-col items-center gap-y-10 bg-[#1F1F1F] pb-5 pt-10'>
      <div className='hidden h-40 w-4/5 justify-between md:flex'>
        <LogoBlock />
        <NavBlock1 />
        <NavBlock2 />
        <SocialBlock handleLanguageChange={handleLanguageChange} />
      </div>
      <div className='flex w-4/5 flex-col gap-y-10 md:hidden'>
        <div className='flex w-full justify-between'>
          <LogoBlock />
          <SocialBlock handleLanguageChange={handleLanguageChange} />
        </div>
        <div className='flex justify-around'>
          <NavBlock1 />
          <NavBlock2 />
        </div>
      </div>
      <VersionBlock />
    </div>
  )
}
